import type { GeofenceAction } from './actions';
import { ActionTypes } from './actionTypes';

import type {
  Coordinate,
  Geofence,
  GeofenceValidationSummary,
} from '@cobliteam/common-types';

import {
  isWebDataSuccess,
  type WebData,
  webDataError,
  webDataFetching,
  webDataNotAsked,
  webDataSuccess,
} from '@cobliteam/api-lib';

import {
  createNamedWrapperReducer,
  type ISpreadsheetState,
  rootReducer,
  spreadsheetReducer,
} from '../HOR';
import { DomainTypes } from '../types';

export interface GeofenceState extends ISpreadsheetState {
  geofencesWebData: WebData<Geofence[]>;
  coordinate?: Coordinate;
  validateGeofencesWebData: WebData<GeofenceValidationSummary>;
  importGeofencesWebData: WebData<void>;
}

export const geofencesInitialState: GeofenceState = {
  spreadsheet: null,
  geofencesWebData: webDataNotAsked(),
  validateGeofencesWebData: webDataNotAsked(),
  importGeofencesWebData: webDataNotAsked(),
};

const geofenceReducer = (
  state: GeofenceState = geofencesInitialState,
  action: GeofenceAction
): GeofenceState => {
  if (!(action.type in ActionTypes)) {
    return state;
  }

  switch (action.type) {
    case ActionTypes.SAVE_GEOFENCE_SUCCEEDED:
      if (isWebDataSuccess(state.geofencesWebData)) {
        const newGeofence = action.payload;
        const updatedGeofenceList = [
          ...state.geofencesWebData.data,
          newGeofence,
        ];
        return {
          ...state,
          geofencesWebData: webDataSuccess(updatedGeofenceList),
        };
      }
      return state;

    case ActionTypes.LOAD_GEOFENCES_SUCCEEDED:
      return {
        ...state,
        geofencesWebData: webDataSuccess(action.payload),
      };

    case ActionTypes.LOAD_GEOFENCES_FAILED:
      return { ...state, geofencesWebData: webDataError() };

    case ActionTypes.DELETE_GEOFENCE_SUCCEEDED:
      // TODO make this validation in saga like SAVE_GEOFENCE_SUCCEEDED
      if (isWebDataSuccess(state.geofencesWebData)) {
        return {
          ...state,
          geofencesWebData: webDataSuccess(
            state.geofencesWebData.data.filter(
              (item) => item.id !== action.payload
            )
          ),
        };
      }
      return state;

    case ActionTypes.VALIDATE_GEOFENCES_REQUEST:
      return { ...state, validateGeofencesWebData: webDataFetching() };

    case ActionTypes.VALIDATE_GEOFENCES_SUCCEEDED:
      return {
        ...state,
        validateGeofencesWebData: webDataSuccess(action.payload),
      };

    case ActionTypes.VALIDATE_GEOFENCES_FAILED:
      return { ...state, validateGeofencesWebData: webDataError() };

    case ActionTypes.IMPORT_GEOFENCES_REQUEST:
      return { ...state, importGeofencesWebData: webDataFetching() };

    case ActionTypes.IMPORT_GEOFENCES_SUCCEEDED:
      return {
        ...state,
        importGeofencesWebData: webDataSuccess(undefined),
      };

    case ActionTypes.IMPORT_GEOFENCES_FAILED:
      return { ...state, importGeofencesWebData: webDataError() };

    case ActionTypes.CLEAR_IMPORT_GEOFENCES:
      return {
        ...state,
        validateGeofencesWebData: webDataNotAsked(),
        importGeofencesWebData: webDataNotAsked(),
        spreadsheet: null,
      };

    default:
      return state;
  }
};

// TODO: Check the geofenceReducer typing issue
export const geofenceTabReducer = rootReducer(
  [
    geofenceReducer,
    createNamedWrapperReducer(spreadsheetReducer, DomainTypes.GEOFENCES),
  ],
  geofencesInitialState
);
